import React, { useEffect, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faLocationDot } from "@fortawesome/free-solid-svg-icons";
import BoxWrapper from "src/Components/WebsiteComponents/BoxWrapper";
import { IOrderDetails } from "src/types/GlobalInterfaces";
import httpRequest from "src/helpers/httpRequest";
import { toast } from "react-toastify";
import { Link, useNavigate, useParams } from "react-router-dom";
import { getLastSegment } from "src/helpers/helperFunctions";
import moment from "moment";
import {
  CURRENCY,
  EXPRESS_DELIVERY_CHARGE,
  ImageBaseUrl,
  STANDARD_DELIVERY_CHARGE,
} from "src/lib/constans";
import PrintStatus from "src/Components/GlobalComponents/PrintStatus";
import NoItemUI from "src/Components/NoItemUI";
import WriteReviewModal from "src/Components/Modals/WriteReviewModal";
import { getIndicators } from "src/Store/redDots";
import { useDispatch } from "react-redux";
import { AppDispatch } from "@store/store";

interface OrderHistory {
  _id: string;
  orderId: string;
  status: string;
  comment: string;
  dateAdded: string;
  createdAt: string;
  updatedAt: string;
  __v: number;
}

function MyOrderDetail() {
  // Global State
  const { type = "", id = "" } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const [isLoader, setIsLoader] = useState<boolean>(false);
  const [item, setItem] = useState<IOrderDetails | null>(null);
  const [history, setHistory] = useState<OrderHistory[]>([]);
  const [isPharma, setIsPharma] = useState<boolean>(false);
  const [isWriteReviewModalOpen, setIsWriteReviewModalOpen] =
    useState<any>(null);
  // API_CALL: Fetch order details by ID
  const fetchOrderById = async (_id: string) => {
    setIsLoader(true);
    const { res, err } = await httpRequest<IOrderDetails>({
      path: `/${type}-order/order/${_id}`,
    });
    if (res) {
      setItem(res); // Update state with fetched order details
      dispatch(getIndicators("/customer/user-indicators"));
    } else {
      toast(err, { type: "error" }); // Show error toast if the request fails
    }
    setIsLoader(false); // Hide loader once data is fetched
  };

  const fetchOrderHistory = async (_id: string) => {
    const path =
      type === "pharma"
        ? `/pharma-order/history/status/${_id}`
        : `/diagnostic-order/history/status/${_id}`;
    console.log(path);
    const { res, err } = await httpRequest<OrderHistory[]>({ path });
    if (res) {
      setHistory(res); // Update state with fetched history
    } else {
      toast(err, { type: "error" }); // Show error toast if the request fails
    }
  };

  useEffect(() => {
    if (id && type) {
      fetchOrderHistory(id);
      fetchOrderById(id);
    }
  }, [id, type]);

  return (
    <div className="w-full bg-banner-white-image 2xl:py-8 py-6">
      <div className="view-area-width box-design">
        <div className="2xl:px-4 2xl:py-8 px-3 py-6 grid grid-cols-1 lg:grid-cols-3 2xl:gap-8 gap-4">
          {/* Left Side */}
          <div className="lg:col-span-2 2xl:space-y-4 space-y-3">
            <p
              className="h-[29px] w-[122px] bg-[#EFF6FF] text-[#2563EE] flex justify-center items-center"
              style={{
                borderRadius: "50px",
              }}
            >
              {item?.serialOrderId
                ? "#" + getLastSegment(item?.serialOrderId)
                : "------"}
            </p>
            <p className="text-[#707070] font-Poppins-Regular font-size-16px">
              Placed on{" "}
              {item?.orderDate
                ? moment(item?.orderDate).format("MMMM D, YYYY [at] h:mm A")
                : "-- -- --"}
            </p>
            <h1 className="font-size-65px lowercase first-letter:capitalize text-[#393939] font-Lexend-Medium">
              {item?.status || ""}
            </h1>
            {/* <p className="text-[#707070] font-Poppins-Regular font-size-16px relative bottom-4">
              Please complete the payment to confirm the order
            </p> */}

            {type === "pharma" && (
              <div className="flex items-center gap-2 text-gray-700 mt-4">
                <span
                  className="h-[38px] w-[38px] bg-[#EFF6FF] flex justify-center items-center"
                  style={{
                    borderRadius: "10px",
                  }}
                >
                  <FontAwesomeIcon
                    icon={faLocationDot}
                    className="text-[#2563EE]"
                  />
                </span>
                <p className="text-[#2F2F31] font-Poppins-Medium font-size-15px">
                  Deliver To: <br />
                  <span className="text-[#5B5B5B] font-Poppins-Regular font-size-15px">
                    {item?.shippingAddress || ""}
                  </span>
                </p>
              </div>
            )}

            {/* Product List */}
            <BoxWrapper
              title="Products"
              extraStyle={{ marginTop: "40px" }}
              extraTextStyle={{
                color: "#393939",
                fontSize: "20px",
                fontFamily: "Poppins-Medium",
              }}
            >
              <div className="w-full flex flex-col 2xl:gap-3 gap-2">
                {item?.orderDetails && item?.orderDetails?.length > 0 ? (
                  item?.orderDetails.map((product, idx) => (
                    <div
                      key={idx}
                      className={`flex 2xl:gap-4 gap-3 items-center ${
                        idx !== item?.orderDetails.length - 1
                          ? "border-b border-[#00000029] pb-2"
                          : ""
                      }`}
                    >
                      <div
                        className={
                          "2xl:w-[73px] w-[65px] aspect-[1/0.9] bg-gradient-to-b from-[#00000029] to-[#dadada2a] border border-[#B2B2B2] 2xl:rounded-[10px] rounded-md p-2"
                        }
                      >
                        <img
                          src={
                            product?.image
                              ? product?.image
                              : product?.productsDetails?.image
                          }
                          alt="product"
                          className="h-full w-full object-contain"
                        />
                      </div>
                      <div className="flex flex-col flex-1 gap-1">
                        <div className="flex items-center justify-between">
                          <Link
                            to={
                              product?.itemType === "package"
                                ? `/diagnostic-product/package/${product?.productId}/view`
                                : product?.itemType === "product"
                                ? `/diagnostic-product/test/${product?.productId}/view`
                                : `/pharmacy-products/${product?.productId}/view`
                            }
                            className="text-[#2F2F31] hover:underline font-Poppins-Regular font-size-17px"
                          >
                            {product?.productName}
                          </Link>
                          {item?.status === "completed" && (
                            <button
                              disabled={product?.review}
                              className="text-[#2F2F31] underline font-Poppins-Medium font-size-17px"
                              onClick={() => setIsWriteReviewModalOpen(product)}
                              id={`review-button-${idx + 1}`}
                            >
                              Review
                            </button>
                          )}
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="font-Poppins-Medium text-[#2F2F31] font-size-18px">
                            {product?.productPrice
                              ? `${CURRENCY} ${product?.productPrice?.toFixed(
                                  2
                                )}`
                              : "N/A"}
                          </span>
                          {type === "pharma" && <span className="text-[#2F2F31] font-Poppins-Medium font-size-17px">
                            
                            Quantity:{" "}
                            {(product?.quantity
                              ? product?.quantity?.toString()
                              : "1"
                            ).padStart(2, "0")}
                          </span>}
                        </div>
                      </div>
                    </div>
                  ))
                ) : (
                  <NoItemUI text="No Products" />
                )}
              </div>
            </BoxWrapper>

            <BoxWrapper
              title="History"
              extraStyle={{ marginTop: "40px" }}
              extraTextStyle={{
                color: "#393939",
                fontSize: "20px",
                fontFamily: "Poppins-Medium",
              }}
            >
              {history && history?.length > 0 ? (
                history.map((data: OrderHistory, index: number) => (
                  <div
                    className={`relative ${
                      index !== history?.length - 1
                        ? "border-b border-[#00000029] pb-2"
                        : ""
                    } ${index !== 0 ? "pt-2" : ""}`}
                  >
                    <div className="flex items-center justify-between">
                      <p className="text-[#6B6B6B] font-Poppins-Regular font-size-16px">
                        {moment(data?.createdAt).format("DD/MM/YYYY hh:mm A")}
                        {/* {moment(data?.dateAdded).format("DD/MM/YYYY hh:mm A")} */}
                      </p>
                      <PrintStatus status={data?.status} />
                    </div>
                    <p className="text-[#2F2F31] font-Poppins-Regular font-size-17px pt-2 pr-[220px]">
                      {data?.comment}
                    </p>
                  </div>
                ))
              ) : (
                <NoItemUI text2="No History" />
              )}
            </BoxWrapper>
          </div>

          {/* Right Side */}

          <div className="space-y-4">
            <div className="bg-white shadow rounded-lg 2xl:p-4 p-3 border">
              <h2
                className="font-Poppins-Medium font-size-18px mb-2 border-b-2 border-[#E6E6E6] text-[#393939] pb-2"
                style={{
                  borderBottom: "1px solid #E6E6E6",
                }}
              >
                Shipment #3214
              </h2>
              <div className="flex items-center gap-2 pt-4 pb-3 flex-wrap">
                {item?.orderDetails && item?.orderDetails?.length > 0 ? (
                  item?.orderDetails.map((product, idx) => (
                    <div
                      key={idx}
                      className="relative 2xl:w-[73px] w-[65px] aspect-[1/0.9] bg-gradient-to-b from-[#00000029] to-[#dadada2a] border border-[#B2B2B2] 2xl:rounded-[10px] rounded-md p-2"
                    >
                      <img
                        src={
                          product?.image
                            ? product?.image
                            : product?.productsDetails?.image
                        }
                        alt="product"
                        className="h-full w-full object-contain"
                      />
                      {type === "pharma" && <span className="absolute top-[-8px] right-[-8px] bg-green-600 text-white 2xl:text-[11px] text-[8px] 2xl:w-[20px] 2xl:h-[20px] w-[15px] h-[15px] flex items-center justify-center rounded-full">
                        x{product?.quantity || 1}
                      </span>}
                    </div>
                  ))
                ) : (
                  <NoItemUI text="No Products" />
                )}
              </div>
            </div>

            <div className="bg-white shadow rounded-lg 2xl:p-4 p-3 border">
              <h2
                className="font-Poppins-Medium font-size-18px mb-2 text-[#393939] pb-2"
                style={{
                  borderBottom: "1px solid #E6E6E6",
                }}
              >
                Order Details
              </h2>
              <div className="2xl:space-y-2 space-y-1">
                <div className="flex justify-between">
                  <span className="text-[#5B5B5B] font-size-15px font-Poppins-Regular pt-2">
                    Order Total
                  </span>
                  <span className="text-[#5B5B5B] font-size-15px font-Poppins-Regular pt-2">
                    {CURRENCY}{" "}
                    {item?.orderDetails && item?.orderDetails?.length > 0
                      ? item?.orderDetails
                          ?.reduce(
                            (acc, curr) =>
                              acc +
                              (curr?.productPrice || 0) * (curr?.quantity || 1),
                            0
                          )
                          .toFixed(2)
                      : 0}
                  </span>
                </div>

                {type === "pharma" && (
                  <div className="flex justify-between">
                    <span className="text-[#5B5B5B] font-size-15px font-Poppins-Regular">
                      Shipping
                    </span>
                    <span className="text-[#5B5B5B] font-size-15px font-Poppins-Regular">
                      {CURRENCY}{" "}
                      {(item?.deliveryType === "instant delivery"
                        ? EXPRESS_DELIVERY_CHARGE
                        : STANDARD_DELIVERY_CHARGE
                      ).toFixed(2)}
                    </span>
                  </div>
                    
                )}

                <div className="flex justify-between font-bold text-lg text-black">
                  <span className="text-[#2F2F31] font-size-17px font-Poppins-Regular">
                    Total Amount
                  </span>
                  <span className="text-[#2F2F31] font-size-17px font-Poppins-Regular">
                    {CURRENCY}{" "}
                    {item?.totalAmount ? item?.totalAmount?.toFixed(2) : "N/A"}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      {isWriteReviewModalOpen && (
        <WriteReviewModal
          onClose={() => setIsWriteReviewModalOpen(null)}
          productData={isWriteReviewModalOpen}
          onSuccess={() => {
            if (item) {
              setItem((prev: any) => ({
                ...prev,
                orderDetails: prev?.orderDetails?.map((product: any) =>
                  product.productId === isWriteReviewModalOpen.productId
                    ? { ...product, review: {} }
                    : product
                ),
              }));
            }
          }}
          id={id}
        />
      )}
    </div>
  );
}

export default MyOrderDetail;
